import React, { useEffect, useState } from "react";
import { Loader2, CalendarOff, Send, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";

const fmtRange = (a, b) => {
  try {
    const f = (d) => formatDate(d);
    return a === b ? f(a) : `${f(a)} – ${f(b)}`;
  } catch { return `${a} – ${b}`; }
};

export default function StudentLeave() {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [reason, setReason] = useState("");
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/student/advance-leaves");
      setLeaves(data);
    } catch { toast.error("Could not load your leave requests"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!from || !to) { toast.error("Please choose the leave dates"); return; }
    if (to < from) { toast.error("End date cannot be before start date"); return; }
    if (reason.trim().length < 3) { toast.error("Please give a clear reason"); return; }
    setSubmitting(true);
    try {
      await api.post("/student/advance-leaves", { date_from: from, date_to: to, reason: reason.trim() });
      toast.success("Leave informed — your teacher will see it on the attendance roster.");
      setReason("");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not submit leave");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6 w-full max-w-full overflow-x-hidden" data-testid="student-leave">
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-1">
          <CalendarOff className="h-5 w-5 text-blue-900" />
          <h2 className="text-xl font-bold text-slate-900">Inform Absence / Advance Leave</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">Let the teacher know in advance when you will be absent. This does not need approval — it simply flags your absence as informed.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="leave-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="mb-1.5 block font-semibold text-slate-700">From</Label>
              <Input type="date" value={from} min={today} onChange={(e) => setFrom(e.target.value)} data-testid="leave-from" required /></div>
            <div><Label className="mb-1.5 block font-semibold text-slate-700">To</Label>
              <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} data-testid="leave-to" required /></div>
          </div>
          <div>
            <Label className="mb-1.5 block font-semibold text-slate-700">Reason</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. Family function / not well" data-testid="leave-reason" required />
          </div>
          <Button type="submit" disabled={submitting} className="bg-blue-900 hover:bg-blue-950" data-testid="leave-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Inform leave</>}
          </Button>
        </form>
      </div>

      <div>
        <h3 className="font-bold text-slate-900 mb-3">My Leave Requests</h3>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>
        ) : leaves.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500" data-testid="leave-empty">
            <Info className="h-7 w-7 mx-auto mb-2 text-slate-400" />No leaves informed yet.
          </div>
        ) : (
          <div className="space-y-2" data-testid="leave-list">
            {leaves.map((l) => (
              <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid={`leave-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800">{fmtRange(l.date_from, l.date_to)}</p>
                  <p className="text-xs text-slate-500 truncate">{l.reason}</p>
                </div>
                {l.source === "teacher" && <Badge className="bg-slate-500 shrink-0">By teacher</Badge>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
